/**
 * Phase 2.C — Per-item visibility mask for the Helper task card.
 *
 * Mask shape is `Partial<Record<HelperVisibleField, boolean>>`.
 * Field present + `true`  → show
 * Field present + `false` → hide
 * Field absent             → default-allow (see {@link DEFAULT_HELPER_VISIBILITY_MASK})
 *
 * Phase 2 scope per C.10: mask gates **display only**. Action capability is
 * still gated by WorkspaceRole + standard permission logic.
 */

export type HelperVisibleField =
  | 'title'
  | 'description'
  | 'completionCriteria'
  | 'priorityChips'
  | 'urgencyStars'
  | 'importanceStars'
  | 'complexityStars'
  | 'weightage'
  | 'scheduleWindow'
  | 'timeWindow'
  | 'estimatedHours'
  | 'actualCost'
  | 'currencyCode'
  | 'requiresProofFile'
  | 'requiresLocationPin'
  | 'targetCoordinates'
  | 'dependencies'
  | 'commentsCount'
  | 'workflowStatus'

export type HelperVisibilityMask = Partial<Record<HelperVisibleField, boolean>>

/** Default per C.9: missing fields are visible. The default mask therefore
 * marks every field allowed. Items that opt into masking can hide specifics. */
export const DEFAULT_HELPER_VISIBILITY_MASK: Readonly<HelperVisibilityMask> = Object.freeze({
  title: true,
  description: true,
  completionCriteria: true,
  priorityChips: true,
  urgencyStars: true,
  importanceStars: true,
  complexityStars: true,
  weightage: true,
  scheduleWindow: true,
  timeWindow: true,
  estimatedHours: true,
  actualCost: true,
  currencyCode: true,
  requiresProofFile: true,
  requiresLocationPin: true,
  targetCoordinates: true,
  dependencies: true,
  commentsCount: true,
  workflowStatus: true,
})

/** Default-allow resolver. Use in card render checks. */
export const isFieldVisibleToHelper = (
  mask: HelperVisibilityMask | undefined | null,
  field: HelperVisibleField,
): boolean => {
  if (!mask) return true
  if (!(field in mask)) return true
  return mask[field] !== false
}

/** Merge a per-item override on top of a workspace default. Per C.7 the per-item
 * value takes precedence; missing keys fall back to the workspace default; if
 * neither defines a key, the resolver above returns true (default-allow). */
export const mergeHelperVisibilityMask = (
  workspaceDefault: HelperVisibilityMask | undefined | null,
  itemOverride: HelperVisibilityMask | undefined | null,
): HelperVisibilityMask => {
  return { ...(workspaceDefault ?? {}), ...(itemOverride ?? {}) }
}

/** Phase 3.D — true when the effective mask hides at least one known field.
 * Used to stamp the `isMasked` audit flag at write time. */
export const maskHasAnyHidden = (mask: HelperVisibilityMask | undefined | null): boolean => {
  if (!mask) return false
  return Object.values(mask).some((value) => value === false)
}

/** Phase 3.C — list of fields a Helper is allowed to see, given an effective
 * mask. Returns the field keys, used by the service-layer redactor. */
export const allowedHelperFields = (
  mask: HelperVisibilityMask | undefined | null,
): ReadonlyArray<HelperVisibleField> => {
  return (Object.keys(DEFAULT_HELPER_VISIBILITY_MASK) as HelperVisibleField[])
    .filter((field) => isFieldVisibleToHelper(mask, field))
}
